import {useState, useEffect} from 'react'
import {useSearchStores} from '@salesforce/commerce-sdk-react'

/**
 * Custom React hook for performing a paginated search of stores using the provided initial parameters and options.
 * Fetches stores in batches (with a maximum limit per API call), accumulates results, and manages loading and error states.
 *
 * @param {Object} initialParameters - The initial search parameters for querying stores (e.g., location, filters).
 * @param {Object} options - Additional options to be passed to the useSearchStores hook (e.g., query options).
 * @returns {Object} An object containing:
 *   - {Object} data: The paginated search result, including:
 *       - {number} limit: The maximum number of stores fetched per API call.
 *       - {Array} data: The accumulated array of all fetched stores.
 *       - {number} offset: The current offset used for pagination.
 *       - {number} total: The total number of stores available from the API.
 *   - {boolean} isLoading: Indicates if the search is currently loading.
 *   - {Error|null} error: Any error encountered during the search, or null if none.
 */
const usePaginatedSearchStores = (initialParameters, options) => {
    const [allStores, setAllStores] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const [offset, setOffset] = useState(0)
    const [total, setTotal] = useState(0)
    const limit = 200 // maximum from single API call

    const {
        data: stores,
        isLoading: isStoresLoading,
        error: storesError
    } = useSearchStores(
        {
            parameters: {
                ...initialParameters,
                offset,
                limit
            }
        },
        options
    )

    useEffect(() => {
        if (storesError) {
            setError(storesError)
            setIsLoading(false)
            return
        }

        if (!isStoresLoading && stores) {
            setAllStores((prevStores) => [...prevStores, ...stores.data])
            setTotal(stores.total || 0)

            if (stores.data.length + offset < stores.total) {
                // Increment offset to fetch the next batch of stores
                setOffset((prevOffset) => prevOffset + limit)
            } else {
                setIsLoading(false)
            }
        }
    }, [isStoresLoading, stores, storesError])

    return {
        data: {
            limit,
            data: allStores,
            offset,
            total
        },
        isLoading,
        error
    }
}

export default usePaginatedSearchStores
